import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  Loader2, Film, Eye, Heart, MessageCircle, Share2, ExternalLink, FileText, TrendingUp,
} from 'lucide-react';
import AppShell from '@/components/AppShell';
import SEO from '@/components/SEO';
import { ProductsInVideos, ShopBadge, ViewProductLink, proxyImg } from '@/components/VideoProducts';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('kylo_token')}` });

const fmt = (n) => {
  const v = Number(n || 0);
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return `${v}`;
};
const toMillis = (ct) => {
  if (!ct) return 0;
  if (/^\d+$/.test(String(ct))) { const n = parseInt(ct, 10); return n < 1e12 ? n * 1000 : n; }
  const t = Date.parse(ct);
  return Number.isNaN(t) ? 0 : t;
};
const fmtDate = (ct) => {
  const ms = toMillis(ct);
  return ms ? new Date(ms).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—';
};

export default function VideosPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transcripts, setTranscripts] = useState({});
  const [transcribingId, setTranscribingId] = useState(null);
  const [transcriptView, setTranscriptView] = useState(null);
  const [filter, setFilter] = useState('all'); // all | ads

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/me/creator-dashboard`, { headers: authHeaders(), timeout: 120000 });
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load your videos');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const videos = data?.videos || [];
  const adsCount = videos.filter((v) => v.is_ad).length;
  const shown = filter === 'ads' ? videos.filter((v) => v.is_ad) : videos;

  const transcribe = async (v) => {
    const vid = v.video_id || v.url;
    if (transcripts[vid]) { setTranscriptView({ label: fmtDate(v.create_time), text: transcripts[vid] }); return; }
    setTranscribingId(vid);
    try {
      const r = await axios.post(`${API}/api/me/transcribe`, { url: v.url, video_id: v.video_id }, { headers: authHeaders(), timeout: 60000 });
      setTranscripts((t) => ({ ...t, [vid]: r.data.transcript }));
      setTranscriptView({ label: fmtDate(v.create_time), text: r.data.transcript });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Transcription failed');
    } finally { setTranscribingId(null); }
  };

  return (
    <AppShell title="Videos">
      <SEO title="Your Videos — Poettik" description="Your recent TikTok videos and their stats." noindex />
      <div className="px-4 sm:px-8 py-6" data-testid="videos-page">
        <div className="flex items-start gap-3 mb-5">
          <span className="w-10 h-10 rounded-[12px] bg-[#FBE4EE] text-[#9D174D] flex items-center justify-center shrink-0">
            <Film className="w-5 h-5" strokeWidth={1.8} />
          </span>
          <div>
            <h2 className="font-display text-xl text-[#1F1B16] leading-tight">Your recent videos</h2>
            <p className="text-sm text-[#6F675D] mt-0.5">The same recent videos from your dashboard — tap any to open on TikTok or transcribe.</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#9D174D]" /></div>
        ) : data?.needs_handle ? (
          <div className="rounded-[16px] border border-[#EFE1D1] bg-[#FBF7F0] p-6 text-center max-w-md" data-testid="videos-needs-handle">
            <TrendingUp className="w-7 h-7 text-[#9D174D] mx-auto mb-3" strokeWidth={1.8} />
            <p className="text-[#1F1B16] font-medium mb-1">Connect your TikTok first</p>
            <p className="text-sm text-[#6F675D] mb-4">Add your TikTok handle on the dashboard to see your videos here.</p>
            <Link to="/creator-dashboard"><Button className="bg-[#1F1B16] text-[#FFFDF9] hover:bg-[#332d24]" data-testid="videos-go-dashboard">Go to dashboard</Button></Link>
          </div>
        ) : videos.length === 0 ? (
          <p className="text-sm text-[#6F675D]" data-testid="videos-empty">No videos posted in this period.</p>
        ) : (
          <>
            <ProductsInVideos videos={videos} />
            <div className="inline-flex rounded-full border border-[#E6DED1] bg-[#FBF7F0] p-0.5 mb-4" data-testid="videos-filter">
              {[{ k: 'all', label: `All (${videos.length})` }, { k: 'ads', label: `Ads only (${adsCount})` }].map((o) => (
                <button
                  key={o.k}
                  type="button"
                  onClick={() => setFilter(o.k)}
                  className={`px-3.5 h-8 rounded-full text-xs font-medium transition-colors ${filter === o.k ? 'bg-[#1F1B16] text-[#FFFDF9]' : 'text-[#6F675D]'}`}
                  data-testid={`videos-filter-${o.k}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            {shown.length === 0 ? (
              <p className="text-sm text-[#6F675D]" data-testid="videos-ads-empty">No ads or paid partnerships in this period.</p>
            ) : (
              <div className="grid grid-cols-3 lg:grid-cols-8 gap-2" data-testid="videos-grid">
                {shown.map((v, i) => {
                  const vid = v.video_id || v.url;
                  const busy = transcribingId === vid;
                  const hasScript = !!transcripts[vid];
                  return (
                    <div key={vid || i} className="rounded-[10px] border border-[#EFE1D1] bg-[#FBF7F0] overflow-hidden" data-testid={`video-${i}`}>
                      <a href={v.url || '#'} target="_blank" rel="noopener noreferrer" className="block relative group">
                        {v.cover ? (
                          <img src={proxyImg(v.cover)} alt="" referrerPolicy="no-referrer" className="w-full aspect-[9/16] object-cover" />
                        ) : (
                          <div className="w-full aspect-[9/16] bg-[#F0E8DB] flex items-center justify-center">
                            <Film className="w-4 h-4 text-[#C9BCA8]" />
                          </div>
                        )}
                        <span className="absolute top-1 right-1 bg-black/55 text-white rounded px-1 py-0.5 inline-flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <ExternalLink className="w-2.5 h-2.5" />
                        </span>
                        {v.is_ad && (
                          <span className="absolute top-1 left-1 bg-[#9D174D] text-white text-[9px] font-bold tracking-wide rounded px-1.5 py-0.5 shadow-sm" title="Ad or paid partnership" data-testid={`video-ad-badge-${i}`}>
                            AD
                          </span>
                        )}
                        {v.product_link && <ShopBadge offset={v.is_ad} testid={`video-shop-badge-${i}`} />}
                      </a>
                      <div className="px-2 py-1.5">
                        <p className="text-[10px] text-[#9D8E7B] mb-1">{fmtDate(v.create_time)}</p>
                        <div className="flex items-center justify-between text-[10px] font-medium text-[#3C362D]">
                          <span className="inline-flex items-center gap-0.5" title="Views"><Eye className="w-3 h-3 text-[#B45309]" strokeWidth={2} />{fmt(v.views)}</span>
                          <span className="inline-flex items-center gap-0.5" title="Likes"><Heart className="w-3 h-3 text-[#9D174D]" strokeWidth={2} />{fmt(v.likes)}</span>
                        </div>
                        <div className="flex items-center justify-between text-[10px] font-medium text-[#3C362D] mt-0.5">
                          <span className="inline-flex items-center gap-0.5" title="Comments"><MessageCircle className="w-3 h-3 text-[#A3762F]" strokeWidth={2} />{fmt(v.comments)}</span>
                          <span className="inline-flex items-center gap-0.5" title="Shares"><Share2 className="w-3 h-3 text-[#1E7A6B]" strokeWidth={2} />{fmt(v.shares)}</span>
                        </div>
                        {v.product_link && <ViewProductLink link={v.product_link} testid={`video-view-product-${i}`} />}
                        <button
                          type="button"
                          onClick={() => transcribe(v)}
                          disabled={busy}
                          className="mt-1.5 w-full inline-flex items-center justify-center gap-1 h-6 rounded-md border border-[#E6DED1] text-[9px] font-semibold text-[#9D174D] hover:bg-[#FBE4EE] disabled:opacity-60 transition-colors"
                          data-testid={`transcribe-${i}`}
                        >
                          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                          {busy ? 'Working…' : (hasScript ? 'View script' : 'Transcribe')}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      <Dialog open={!!transcriptView} onOpenChange={(o) => { if (!o) setTranscriptView(null); }}>
        <DialogContent className="bg-[#FFFDF9] border-[#E6DED1] sm:max-w-lg" data-testid="transcript-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#1F1B16]">Video transcript{transcriptView?.label ? ` — ${transcriptView.label}` : ''}</DialogTitle>
            <DialogDescription className="text-[#6F675D]">Pulled from this TikTok. Copy it into Create Script to reuse the structure.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto rounded-[12px] bg-[#FBF7F0] border border-[#EFE1D1] p-3 text-sm text-[#3C362D] whitespace-pre-wrap" data-testid="transcript-text">{transcriptView?.text}</div>
          <Button onClick={() => { navigator.clipboard.writeText(transcriptView?.text || ''); toast.success('Transcript copied'); }} className="bg-[#1F1B16] text-[#FFFDF9] hover:bg-[#332d24]" data-testid="transcript-copy">Copy transcript</Button>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
